import { useState } from "react"
import { Link } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion"
import { cn } from "@/lib/utils"
import {
  CalendarIcon,
  ClockIcon,
  UsersIcon,
  CheckCircleIcon,
  PlayCircleIcon,
  ArrowLeftIcon,
} from "lucide-react"

type CohortStatus = "enrolling" | "starting-soon" | "in-progress" | "completed"
type TabType = "overview" | "curriculum" | "instructor" | "faq"

interface Module {
  id: string
  title: string
  chapterCount: number
  duration: string
  chapters: string[]
}

interface Instructor {
  name: string
  photo: string
  bio: string
  experience: string[]
}

interface FAQ {
  question: string
  answer: string
}

interface CohortDetailPageProps {
  /**
   * Cohort name
   */
  name?: string

  /**
   * Cohort status
   */
  status?: CohortStatus

  /**
   * Banner image URL
   */
  bannerImage?: string

  /**
   * Price
   */
  price?: string

  /**
   * Start date
   */
  startDate?: string

  /**
   * Duration
   */
  duration?: string

  /**
   * Number of enrolled students
   */
  enrolled?: number

  /**
   * Maximum capacity
   */
  capacity?: number

  /**
   * Description
   */
  description?: string

  /**
   * What you'll learn
   */
  learningPoints?: string[]

  /**
   * Who this is for
   */
  targetAudience?: string[]

  /**
   * Curriculum modules
   */
  modules?: Module[]

  /**
   * Instructor information
   */
  instructor?: Instructor

  /**
   * FAQ items
   */
  faqs?: FAQ[]

  /**
   * Is user authenticated
   */
  isAuthenticated?: boolean

  /**
   * Is user already enrolled in this cohort
   */
  isEnrolled?: boolean

  /**
   * Is enrollment currently open (status=enrolling, not full, not past close date)
   */
  enrollmentOpen?: boolean

  /**
   * URL to enroll (checkout or signup)
   */
  enrollUrl?: string | null

  /**
   * URL to founder dashboard
   */
  dashboardUrl?: string

  /**
   * Whether to show the price
   */
  showPrice?: boolean

  /**
   * Cohort objective
   */
  objective?: string

  /**
   * Enroll callback
   */
  onEnroll?: () => void

  /**
   * Additional CSS classes
   */
  className?: string
}

// Default data
const defaultModules: Module[] = [
  {
    id: "1",
    title: "Module 1: Understanding Customer Discovery",
    chapterCount: 5,
    duration: "2 hours",
    chapters: [
      "Introduction to Customer Discovery",
      "Why Most Startups Fail",
      "The Problem-Solution Framework",
      "Identifying Your Target Customer",
      "Setting Up Your Discovery Process"
    ]
  },
  {
    id: "2",
    title: "Module 2: Conducting Effective Interviews",
    chapterCount: 6,
    duration: "2.5 hours",
    chapters: [
      "Interview Fundamentals",
      "Crafting the Right Questions",
      "Active Listening Techniques",
      "Avoiding Leading Questions",
      "Taking Effective Notes",
      "Practice Interview Sessions"
    ]
  },
  {
    id: "3",
    title: "Module 3: Analyzing Interview Data",
    chapterCount: 4,
    duration: "1.5 hours",
    chapters: [
      "Organizing Your Findings",
      "Identifying Patterns",
      "Validating Pain Points",
      "Creating Customer Personas"
    ]
  },
  {
    id: "4",
    title: "Module 4: Iterating on Your Solution",
    chapterCount: 5,
    duration: "2 hours",
    chapters: [
      "From Insights to Action",
      "Pivoting vs. Persevering",
      "Building Your MVP",
      "Testing Assumptions",
      "Measuring Progress"
    ]
  }
]

const defaultInstructor: Instructor = {
  name: "Sarah Chen",
  photo: "https://github.com/yahyabedirhan.png",
  bio: "Sarah is a 3-time founder with 2 successful exits. She's helped over 500 founders validate their ideas and build products customers love. Previously led product at a YC-backed startup.",
  experience: [
    "Founded 3 startups (2 successful exits)",
    "Conducted 1000+ customer interviews",
    "Mentored 500+ founders",
    "Former Product Lead at YC-backed startup",
    "Featured speaker at Startup Grind"
  ]
}

const defaultFAQs: FAQ[] = [
  {
    question: "What if I miss a live session?",
    answer: "All sessions are recorded and available within 24 hours. You'll have lifetime access to all recordings and materials."
  },
  {
    question: "How much time should I dedicate each week?",
    answer: "Plan for 3-4 hours per week: 1.5 hours for live sessions, 1-2 hours for assignments, and 30 minutes for community engagement."
  },
  {
    question: "Do I need to have a startup idea already?",
    answer: "No! This cohort is perfect whether you have a specific idea or are still exploring. We'll help you validate or discover the right opportunity."
  },
  {
    question: "What's the refund policy?",
    answer: "We offer a 100% money-back guarantee within the first 7 days if you're not satisfied with the program."
  },
  {
    question: "Will I get a certificate?",
    answer: "Yes! Upon completion, you'll receive a certificate of completion and can showcase your work in our alumni directory."
  }
]

export function CohortDetailPage({
  name = "Customer Discovery Fundamentals",
  status = "enrolling",
  bannerImage = "https://images.unsplash.com/photo-1552664730-d307ca884978?w=1600&h=600&fit=crop",
  price = "$499",
  startDate = "March 15, 2024",
  duration = "8 weeks",
  enrolled = 12,
  capacity = 20,
  description = "Master the art of customer discovery and learn how to validate your startup idea before building. This hands-on cohort will teach you proven frameworks used by successful founders to identify real problems, conduct effective interviews, and iterate based on customer feedback.",
  learningPoints = [
    "How to identify and validate real customer problems",
    "Proven interview techniques to uncover hidden insights",
    "Framework for analyzing and synthesizing customer data",
    "How to iterate on your solution based on feedback",
    "Building customer personas and journey maps",
    "Measuring validation progress and knowing when to pivot"
  ],
  targetAudience = [
    "Early-stage founders validating their first idea",
    "Product managers looking to improve discovery skills",
    "Entrepreneurs who've failed before and want to do it right",
    "Anyone building a product and wants to ensure product-market fit"
  ],
  modules = defaultModules,
  instructor = defaultInstructor,
  faqs = defaultFAQs,
  isAuthenticated = false,
  isEnrolled = false,
  enrollmentOpen = false,
  enrollUrl,
  dashboardUrl,
  showPrice = true,
  objective,
  onEnroll,
  className
}: CohortDetailPageProps) {
  const [activeTab, setActiveTab] = useState<TabType>("overview")
  const [showMobileCTA, setShowMobileCTA] = useState(false)

  const spotsAvailable = capacity - enrolled
  const enrollmentPercentage = (enrolled / capacity) * 100

  const getStatusBadge = () => {
    const variants: Record<string, { label: string; className: string }> = {
      enrolling: { label: "Enrolling Now", className: "bg-green-500 text-white" },
      "starting-soon": { label: "Starting Soon", className: "bg-primary text-primary-foreground" },
      "in-progress": { label: "In Progress", className: "bg-yellow-500 text-white" },
      completed: { label: "Completed", className: "bg-gray-500 text-white" },
      draft: { label: "Draft", className: "bg-gray-400 text-white" }
    }
    return variants[status] || { label: status, className: "bg-gray-400 text-white" }
  }

  const handleEnroll = () => {
    if (onEnroll) {
      onEnroll()
    } else {
      console.log("Enroll clicked")
    }
  }

  const tabs: { id: TabType; label: string }[] = [
    { id: "overview", label: "Overview" },
    { id: "curriculum", label: "Curriculum" },
    { id: "instructor", label: "Instructor" },
    { id: "faq", label: "FAQ" }
  ]

  return (
    <div className={cn("min-h-screen bg-background", className)}>
      {/* Hero Section */}
      <div className="relative w-full h-[300px] bg-muted overflow-hidden">
        <img
          src={bannerImage}
          alt={name}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-black/20" />
        <div className="absolute inset-0 flex flex-col justify-end p-6 md:p-8">
          <div className="container mx-auto max-w-7xl">
            <Link
              to="/cohorts"
              className="inline-flex items-center gap-2 text-white/90 hover:text-white mb-4 transition-colors"
            >
              <ArrowLeftIcon className="w-4 h-4" />
              <span className="text-sm font-medium">Back to Cohorts</span>
            </Link>
            <div className="flex items-start gap-4">
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white drop-shadow-lg flex-1">
                {name}
              </h1>
              <Badge className={cn("text-sm font-semibold", getStatusBadge().className)}>
                {getStatusBadge().label}
              </Badge>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto max-w-7xl px-4 py-8 md:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Content Area (2/3) */}
          <div className="lg:col-span-2 space-y-8">
            {/* Tabs */}
            <div className="border-b border-border">
              <div className="flex gap-6 overflow-x-auto">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={cn(
                      "pb-4 px-1 text-sm font-medium border-b-2 transition-colors whitespace-nowrap",
                      activeTab === tab.id
                        ? "border-primary text-foreground"
                        : "border-transparent text-muted-foreground hover:text-foreground"
                    )}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Tab Content */}
            <div className="space-y-8">
              {/* Overview Tab */}
              {activeTab === "overview" && (
                <div className="space-y-8">
                  {/* Description */}
                  <div>
                    <h2 className="text-2xl font-bold text-foreground mb-4">
                      About This Cohort
                    </h2>
                    <p className="text-foreground leading-relaxed">
                      {description}
                    </p>
                  </div>

                  {/* What You'll Learn */}
                  <div>
                    <h2 className="text-2xl font-bold text-foreground mb-4">
                      What You'll Learn
                    </h2>
                    <ul className="space-y-3">
                      {learningPoints.map((point, index) => (
                        <li key={index} className="flex items-start gap-3">
                          <CheckCircleIcon className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                          <span className="text-foreground">{point}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Who This Is For */}
                  <div>
                    <h2 className="text-2xl font-bold text-foreground mb-4">
                      Who This Is For
                    </h2>
                    <ul className="space-y-3">
                      {targetAudience.map((audience, index) => (
                        <li key={index} className="flex items-start gap-3">
                          <div className="w-2 h-2 rounded-full bg-primary flex-shrink-0 mt-2" />
                          <span className="text-foreground">{audience}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              {/* Curriculum Tab */}
              {activeTab === "curriculum" && (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-2xl font-bold text-foreground mb-2">
                      Course Curriculum
                    </h2>
                    <p className="text-muted-foreground">
                      {modules.reduce((acc, m) => acc + m.chapterCount, 0)} chapters across {modules.length} modules
                    </p>
                  </div>

                  <Accordion type="single" collapsible className="space-y-4">
                    {modules.map((module) => (
                      <AccordionItem
                        key={module.id}
                        value={module.id}
                        className="border border-border rounded-lg px-6 bg-card"
                      >
                        <AccordionTrigger className="hover:no-underline py-4">
                          <div className="flex items-start justify-between w-full pr-4">
                            <div className="text-left">
                              <h3 className="text-lg font-semibold text-foreground mb-1">
                                {module.title}
                              </h3>
                              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                                <span className="flex items-center gap-1">
                                  <PlayCircleIcon className="w-4 h-4" />
                                  {module.chapterCount} chapters
                                </span>
                                <span className="flex items-center gap-1">
                                  <ClockIcon className="w-4 h-4" />
                                  {module.duration}
                                </span>
                              </div>
                            </div>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent className="pt-2 pb-4">
                          <ul className="space-y-2">
                            {module.chapters.map((chapter, index) => (
                              <li
                                key={index}
                                className="flex items-center gap-3 text-foreground py-2"
                              >
                                <span className="text-muted-foreground text-sm font-medium w-6">
                                  {index + 1}.
                                </span>
                                <span>{chapter}</span>
                              </li>
                            ))}
                          </ul>
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </div>
              )}

              {/* Instructor Tab */}
              {activeTab === "instructor" && instructor && (
                <div className="space-y-6">
                  <h2 className="text-2xl font-bold text-foreground">
                    Your Instructor
                  </h2>

                  <div className="flex flex-col sm:flex-row gap-6">
                    <img
                      src={instructor.photo}
                      alt={instructor.name}
                      className="w-32 h-32 rounded-full object-cover border-4 border-border"
                    />
                    <div className="flex-1">
                      <h3 className="text-xl font-semibold text-foreground mb-2">
                        {instructor.name}
                      </h3>
                      <p className="text-foreground leading-relaxed mb-4">
                        {instructor.bio}
                      </p>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-foreground mb-3">
                      Experience Highlights
                    </h3>
                    <ul className="space-y-2">
                      {instructor.experience.map((item, index) => (
                        <li key={index} className="flex items-start gap-3">
                          <CheckCircleIcon className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                          <span className="text-foreground">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              {/* FAQ Tab */}
              {activeTab === "faq" && (
                <div className="space-y-6">
                  <h2 className="text-2xl font-bold text-foreground">
                    Frequently Asked Questions
                  </h2>

                  <Accordion type="single" collapsible className="space-y-4">
                    {faqs.map((faq, index) => (
                      <AccordionItem
                        key={index}
                        value={`faq-${index}`}
                        className="border border-border rounded-lg px-6 bg-card"
                      >
                        <AccordionTrigger className="hover:no-underline py-4 text-left">
                          <span className="text-lg font-semibold text-foreground pr-4">
                            {faq.question}
                          </span>
                        </AccordionTrigger>
                        <AccordionContent className="pt-2 pb-4">
                          <p className="text-foreground leading-relaxed">
                            {faq.answer}
                          </p>
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </div>
              )}
            </div>
          </div>

          {/* Sidebar (1/3) - Sticky on Desktop */}
          <div className="lg:col-span-1">
            <div className="sticky top-8 space-y-6">
              {/* Info Card */}
              <div className="bg-card border border-border rounded-lg p-6 space-y-6">
                {/* Price */}
                {showPrice && price && (
                  <div>
                    <div className="text-4xl font-bold text-foreground mb-1">
                      {price}
                    </div>
                    <p className="text-sm text-muted-foreground">
                      One-time payment
                    </p>
                  </div>
                )}

                {/* Details */}
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <CalendarIcon className="w-5 h-5 text-muted-foreground" />
                    <div>
                      <div className="text-sm font-medium text-foreground">
                        Start Date
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {startDate}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <ClockIcon className="w-5 h-5 text-muted-foreground" />
                    <div>
                      <div className="text-sm font-medium text-foreground">
                        Duration
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {duration}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <UsersIcon className="w-5 h-5 text-muted-foreground" />
                    <div>
                      <div className="text-sm font-medium text-foreground">
                        Spots Available
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {spotsAvailable}/{capacity} spots left
                      </div>
                    </div>
                  </div>
                </div>

                {/* Enrollment Progress */}
                <div>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-muted-foreground">Enrolled</span>
                    <span className="font-medium text-foreground">
                      {enrolled}/{capacity}
                    </span>
                  </div>
                  <div className="w-full bg-muted rounded-full h-2">
                    <div
                      className="bg-primary rounded-full h-2 transition-all"
                      style={{ width: `${enrollmentPercentage}%` }}
                    />
                  </div>
                </div>

                {/* CTA Button */}
                {isEnrolled ? (
                  <a href={dashboardUrl || "/dashboard"} className="w-full">
                    <Button className="w-full" size="lg" variant="outline">
                      Go to Dashboard
                    </Button>
                  </a>
                ) : enrollmentOpen && enrollUrl ? (
                  <a href={enrollUrl} className="w-full">
                    <Button className="w-full" size="lg">
                      {isAuthenticated ? "Enroll Now" : "Sign Up to Enroll"}
                    </Button>
                  </a>
                ) : (
                  <Button className="w-full" size="lg" disabled>
                    {status === "completed" ? "Cohort Completed" :
                     status === "in-progress" ? "Enrollment Closed" :
                     "Enrollment Closed"}
                  </Button>
                )}

                {/* Additional Info */}
                <div className="pt-4 border-t border-border space-y-2 text-sm text-muted-foreground">
                  <div className="flex items-center gap-2">
                    <CheckCircleIcon className="w-4 h-4 text-green-500" />
                    <span>Lifetime access to materials</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircleIcon className="w-4 h-4 text-green-500" />
                    <span>Certificate of completion</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircleIcon className="w-4 h-4 text-green-500" />
                    <span>7-day money-back guarantee</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Sticky Bottom CTA - only show when enrollment is open or user is enrolled */}
      {(enrollmentOpen || isEnrolled) && (
        <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-card border-t border-border p-4 shadow-lg z-50">
          <div className="flex items-center justify-between gap-4">
            <div>
              {showPrice && price && (
                <div className="text-2xl font-bold text-foreground">
                  {price}
                </div>
              )}
              <div className="text-xs text-muted-foreground">
                {isEnrolled ? "You're enrolled" : `${spotsAvailable} spots left`}
              </div>
            </div>
            {isEnrolled ? (
              <a href={dashboardUrl || "/dashboard"} className="flex-1 max-w-xs">
                <Button size="lg" variant="outline" className="w-full">
                  Go to Dashboard
                </Button>
              </a>
            ) : (
              <a href={enrollUrl!} className="flex-1 max-w-xs">
                <Button size="lg" className="w-full">
                  {isAuthenticated ? "Enroll Now" : "Sign Up to Enroll"}
                </Button>
              </a>
            )}
          </div>
        </div>
      )}

      {/* Add padding to bottom on mobile to account for sticky CTA */}
      {(enrollmentOpen || isEnrolled) && <div className="lg:hidden h-24" />}
    </div>
  )
}